










































































import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, toRefs } from '@vue/composition-api';
import { IconSize } from '@getaccept/lib-ui';
import type { ChatParticipant, LastReadBy, Message } from '../types';
import { ChatHelper } from '../helpers/chat.helper';
import ChatMessageSender from './ChatMessageSender.vue';
import ChatMessageOther from './ChatMessageOther.vue';
import ChatMessageSystem from './ChatMessageSystem.vue';
import ChatTimestamp from './ChatTimestamp.vue';
import MessageReadBy from './MessageReadBy.vue';
import ChatReplyMessage from './ChatReplyMessage.vue';

export default defineComponent({
  name: 'ChatMessage',
  components: {
    ChatMessageSender,
    ChatMessageOther,
    ChatMessageSystem,
    ChatReplyMessage,
    ChatTimestamp,
    MessageReadBy,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      default: undefined,
    },
    recipients: {
      type: Array as PropType<Array<Recipient>>,
      default: () => [],
    },
    message: {
      type: Object as PropType<Message>,
      default: undefined,
    },
    messages: {
      type: Array as PropType<Array<Message>>,
      default: () => [],
    },
    owner: {
      type: Object as PropType<ChatParticipant>,
      default: undefined as ChatParticipant,
    },
    lastReads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
    newChat: {
      type: Boolean,
    },
  },
  emits: ['read', 'delete', 'reply'],
  setup(props, { emit }) {
    const { recipients, user, message, messages, lastReads, owner } = toRefs(props);

    const {
      name,
      thumbUrl,
      date,
      time,
      fullTimestamp,
      isBySender,
      replyMessage,
    } = ChatHelper.getMessageState(
      message.value,
      user.value,
      recipients.value,
      messages.value,
      owner.value
    );

    const previousMessage = ChatHelper.getPreviousMessage(message.value, messages.value);
    const showTimestamp = ChatHelper.showTimestamp(previousMessage, message.value.createdAt);

    const isFirstMessageInBlock = computed(() =>
      ChatHelper.isFirstMessageInBlock(previousMessage, message.value)
    );

    const isLastMessageInBlock = computed(() => {
      const lastMessage = ChatHelper.getLastMessage(messages.value);
      const nextMessage = ChatHelper.getNextMessage(message.value, messages.value);
      return ChatHelper.isLastMessageInBlock(message.value, lastMessage.id, nextMessage);
    });

    const handleRead = () => {
      emit('read', message.value.id);
    };

    const handleDelete = (): void => {
      emit('delete', message.value.id);
    };

    const handleReply = (): void => {
      emit('reply', message.value);
    };

    const messageLastReads = computed(() =>
      lastReads.value.filter(({ messageId }) => messageId === message.value.id)
    );

    const lastReadsExcludingSender = computed(() =>
      messageLastReads.value.filter(
        ({ readBy }) => !ChatHelper.isSameParticipant(message.value.owner, readBy.participant)
      )
    );

    return {
      name,
      thumbUrl,
      date,
      time,
      fullTimestamp,
      isBySender,
      showTimestamp,
      isFirstMessageInBlock,
      isLastMessageInBlock,
      replyMessage,
      handleRead,
      messageLastReads,
      lastReadsExcludingSender,
      handleDelete,
      IconSize,
      handleReply,
    };
  },
});
